import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DollarSign, Percent, Save, ArrowLeft, Calculator, Layers,
  Hash, Circle, Info, Shirt, Search, X, Plus, GripVertical, ChevronUp, ChevronDown, ToggleLeft, ToggleRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default Settings - ALL PRICES ARE GST-INCLUSIVE
const DEFAULT_SETTINGS = {
  print_prices: {
    xs: 15,
    s: 17,
    m: 20,
    large: 25,
    xl: 30,
  },
  quantity_discounts: {
    qty_2_3: 10,   // 10% off for 2-3 garments
    qty_4_6: 20,   // 20% off for 4-6 garments
    qty_7_10: 25,  // 25% off for 7-10 garments
  },
  additional_print_discount: {
    value: 5,  // $5 off per additional print
  },
  byo_pricing: {
    one_print: 35,
    two_prints: 50,
    three_prints: 65,
    additional_print: 15,  // Per print after 3
  },
  rounding: {
    mode: "none",
  },
  retail_presets: [],  // V2: List of preset items
};

const ROUNDING_OPTIONS = [
  { value: "none", label: "No rounding" },
  { value: "nearest_1", label: "Round to nearest $1" },
  { value: "up_1", label: "Round up to nearest $1" },
  { value: "nearest_5", label: "Round to nearest $5" },
];

// Migrate old format settings to V2 format
const migrateSettingsToV2 = (data) => {
  const result = { ...data };
  
  // Migrate print_prices: cap/small/medium/large/extra_large -> xs/s/m/large/xl
  if (result.print_prices) {
    const oldPrices = result.print_prices;
    if ('cap' in oldPrices || 'small' in oldPrices) {
      // Old format detected - use V2 defaults
      result.print_prices = { ...DEFAULT_SETTINGS.print_prices };
    }
  }
  
  // Migrate quantity_discounts: {qty_2: {mode, value}, ...} -> {qty_2_3: number, ...}
  if (result.quantity_discounts) {
    const oldQty = result.quantity_discounts;
    if ('qty_2' in oldQty || (typeof Object.values(oldQty)[0] === 'object' && 'mode' in (Object.values(oldQty)[0] || {}))) {
      // Old format detected - use V2 defaults
      result.quantity_discounts = { ...DEFAULT_SETTINGS.quantity_discounts };
    }
  }
  
  // Migrate additional_print_discount: {mode, value} -> {value}
  if (result.additional_print_discount) {
    const oldDiscount = result.additional_print_discount;
    if ('mode' in oldDiscount) {
      // Old format - use V2 default
      result.additional_print_discount = { value: DEFAULT_SETTINGS.additional_print_discount.value };
    }
  }
  
  return result;
};

export default function RetailPricing() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Product search for presets - keyed by preset ID
  const [products, setProducts] = useState([]);
  const [activePresetSearch, setActivePresetSearch] = useState(null);
  const [presetSearchTerms, setPresetSearchTerms] = useState({});
  
  // New preset form
  const [newPresetLabel, setNewPresetLabel] = useState("");

  // Search products for a specific preset
  const searchProducts = async (presetId, searchTerm) => {
    setPresetSearchTerms({ ...presetSearchTerms, [presetId]: searchTerm });
    if (searchTerm.length >= 2) {
      try {
        const res = await axios.get(`${API}/products`, {
          params: { search: searchTerm, active_only: true, limit: 10 },
        });
        setProducts(res.data.products || []);
        setActivePresetSearch(presetId);
      } catch (error) {
        console.error("Failed to search products", error);
      }
    } else {
      setActivePresetSearch(null);
    }
  };

  // Select product for a preset
  const selectPresetProduct = (presetId, product) => {
    const updatedPresets = (settings.retail_presets || []).map(p => 
      p.id === presetId 
        ? {
            ...p,
            product_id: product.id,
            product_name: `${product.brand || ""} ${product.product_name || ""}`.trim(),
            retail_price: product.retail_price || product.default_cost || 0,
          }
        : p
    );
    setSettings({ ...settings, retail_presets: updatedPresets });
    setPresetSearchTerms({ ...presetSearchTerms, [presetId]: "" });
    setActivePresetSearch(null);
  };

  // Clear product mapping for a preset
  const clearPresetProduct = (presetId) => {
    const updatedPresets = (settings.retail_presets || []).map(p => 
      p.id === presetId 
        ? { ...p, product_id: null, product_name: "", retail_price: 0 }
        : p
    );
    setSettings({ ...settings, retail_presets: updatedPresets });
  };

  // Add new preset
  const addPreset = () => {
    if (!newPresetLabel.trim()) return;
    
    const maxOrder = Math.max(0, ...(settings.retail_presets || []).map(p => p.sort_order || 0));
    const newPreset = {
      id: crypto.randomUUID(),
      label: newPresetLabel.trim(),
      product_id: null,
      product_name: "",
      retail_price: 0,
      sort_order: maxOrder + 1,
      enabled: true,
    };
    setSettings({ ...settings, retail_presets: [...(settings.retail_presets || []), newPreset] });
    setNewPresetLabel("");
  };

  // Delete preset
  const deletePreset = (presetId) => {
    const updatedPresets = (settings.retail_presets || []).filter(p => p.id !== presetId);
    setSettings({ ...settings, retail_presets: updatedPresets });
  };

  // Toggle preset enabled/disabled
  const togglePresetEnabled = (presetId) => {
    const updatedPresets = (settings.retail_presets || []).map(p => 
      p.id === presetId ? { ...p, enabled: !p.enabled } : p
    );
    setSettings({ ...settings, retail_presets: updatedPresets });
  };

  // Move preset up/down in order
  const movePreset = (presetId, direction) => {
    const presets = [...(settings.retail_presets || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = presets.findIndex(p => p.id === presetId);
    if (idx < 0) return;
    
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= presets.length) return;
    
    // Swap sort orders
    const tempOrder = presets[idx].sort_order;
    presets[idx].sort_order = presets[swapIdx].sort_order;
    presets[swapIdx].sort_order = tempOrder;
    
    setSettings({ ...settings, retail_presets: presets });
  };

  // Update preset label
  const updatePresetLabel = (presetId, newLabel) => {
    const updatedPresets = (settings.retail_presets || []).map(p => 
      p.id === presetId ? { ...p, label: newLabel } : p
    );
    setSettings({ ...settings, retail_presets: updatedPresets });
  };

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/retail-pricing`);
        // Migrate old format and merge with defaults
        const migratedData = migrateSettingsToV2(res.data);
        setSettings({
          print_prices: migratedData.print_prices || DEFAULT_SETTINGS.print_prices,
          quantity_discounts: migratedData.quantity_discounts || DEFAULT_SETTINGS.quantity_discounts,
          additional_print_discount: migratedData.additional_print_discount || DEFAULT_SETTINGS.additional_print_discount,
          byo_pricing: migratedData.byo_pricing || DEFAULT_SETTINGS.byo_pricing,
          rounding: migratedData.rounding || DEFAULT_SETTINGS.rounding,
          retail_presets: migratedData.retail_presets || DEFAULT_SETTINGS.retail_presets,
        });
      } catch (error) {
        console.error("Failed to load retail pricing settings", error);
        toast.error("Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Save settings
  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/retail-pricing`, settings);
      toast.success("Retail pricing settings saved");
    } catch (error) {
      console.error("Failed to save settings", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Update nested settings
  const updatePrintPrice = (key, value) => {
    setSettings({
      ...settings,
      print_prices: {
        ...settings.print_prices,
        [key]: parseFloat(value) || 0,
      },
    });
  };

  const updateQtyDiscount = (key, value) => {
    setSettings({
      ...settings,
      quantity_discounts: {
        ...settings.quantity_discounts,
        [key]: parseFloat(value) || 0,
      },
    });
  };

  const updateAdditionalDiscount = (value) => {
    setSettings({
      ...settings,
      additional_print_discount: {
        value: parseFloat(value) || 0,
      },
    });
  };

  const updateBYOPricing = (key, value) => {
    setSettings({
      ...settings,
      byo_pricing: {
        ...settings.byo_pricing,
        [key]: parseFloat(value) || 0,
      },
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Admin access required</p>
            <Link to="/retail">
              <Button className="mt-4">Back to Retail Calculator</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate worked example: AS Colour Staple Tee ($30), 3 prints: S, L, S
  const workedExample = (() => {
    const p = settings.print_prices;
    const additionalDiscount = settings.additional_print_discount.value;
    
    // 3 prints: S ($17), L ($25), S ($17)
    const print1 = p.s;  // First print full price
    const print2 = Math.max(0, p.large - additionalDiscount);  // Second print (L) with discount
    const print3 = Math.max(0, p.s - additionalDiscount);  // Third print (S) with discount
    
    const printTotal = print1 + print2 + print3;
    const garmentPrice = 30; // AS Colour Staple Tee
    const total = printTotal + garmentPrice;
    
    return { print1, print2, print3, printTotal, garmentPrice, total };
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-40">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <Badge variant="secondary" className="text-sm font-medium">
                <DollarSign className="w-3.5 h-3.5 mr-1" />
                Retail Pricing Settings
              </Badge>
              <p className="text-xs text-muted-foreground mt-0.5">V2 - Size-based pricing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/retail">
              <Button variant="outline" size="sm">
                Back to Calculator
              </Button>
            </Link>
            <Button onClick={saveSettings} disabled={isSaving} size="sm">
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-screen-lg mx-auto p-4 sm:p-6 pb-20">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* V2 Info Banner */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Retail Pricing V2 - Calculation Order</p>
                    <ol className="list-decimal list-inside space-y-0.5 text-blue-700">
                      <li>Sum print prices per garment (by size tier)</li>
                      <li>Apply additional print discount (-${settings.additional_print_discount.value} per extra print)</li>
                      <li>Multiply by quantity</li>
                      <li>Apply quantity discount (% off print total only)</li>
                      <li>Add garment retail price (not discounted)</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Print Size Pricing */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Print Size Pricing
                  </CardTitle>
                  <CardDescription>
                    Base price for each print size tier
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: "xs", label: "XS - Extra Small" },
                    { key: "s", label: "S - Small" },
                    { key: "m", label: "M - Medium" },
                    { key: "large", label: "L - Large" },
                    { key: "xl", label: "XL - Extra Large" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{label}</Label>
                      <div className="relative w-28">
                        <DollarSign className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={settings.print_prices[key]}
                          onChange={(e) => updatePrintPrice(key, e.target.value)}
                          className="pl-8 font-mono text-right"
                          data-testid={`print-price-${key}`}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Quantity Discounts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Hash className="w-5 h-5" />
                    Quantity Discounts
                  </CardTitle>
                  <CardDescription>
                    Percentage discount applied to <strong>print total only</strong>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: "qty_2_3", label: "2-3 garments" },
                    { key: "qty_4_6", label: "4-6 garments" },
                    { key: "qty_7_10", label: "7-10 garments" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50/50">
                      <Label className="text-sm font-medium">{label}</Label>
                      <div className="relative w-24">
                        <Percent className="absolute right-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          value={settings.quantity_discounts[key]}
                          onChange={(e) => updateQtyDiscount(key, e.target.value)}
                          className="pr-8 font-mono text-right"
                          data-testid={`qty-discount-${key}`}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground mt-2">
                    Note: Quantity discount applies to print total only. Garment price is NOT discounted.
                  </p>
                </CardContent>
              </Card>

              {/* Additional Print Discount */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Additional Print Discount
                  </CardTitle>
                  <CardDescription>
                    Fixed discount for 2nd, 3rd, etc. prints on each garment
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Discount per additional print</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.additional_print_discount.value}
                        onChange={(e) => updateAdditionalDiscount(e.target.value)}
                        className="pl-8 font-mono"
                        data-testid="additional-discount-value"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      First print = full price. Each additional print = size price - ${settings.additional_print_discount.value} (floor at $0)
                    </p>
                  </div>
                  
                  {/* Preview */}
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Example: L print (${settings.print_prices.large})</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>1st print</span>
                        <span className="font-mono">${settings.print_prices.large.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-700">
                        <span>2nd print</span>
                        <span className="font-mono">
                          ${Math.max(0, settings.print_prices.large - settings.additional_print_discount.value).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-green-700">
                        <span>3rd print</span>
                        <span className="font-mono">
                          ${Math.max(0, settings.print_prices.large - settings.additional_print_discount.value).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* BYO Pricing (Separate System) */}
              <Card className="border-orange-200 bg-orange-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-800">
                    <Calculator className="w-5 h-5" />
                    BYO Garment Pricing
                  </CardTitle>
                  <CardDescription className="text-orange-700">
                    Separate pricing for customer-supplied garments (flat rate by print count)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "one_print", label: "1 Print" },
                      { key: "two_prints", label: "2 Prints" },
                      { key: "three_prints", label: "3 Prints" },
                      { key: "additional_print", label: "Each Additional" },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs text-orange-700">{label}</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2.5 top-2.5 w-4 h-4 text-orange-400" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={settings.byo_pricing?.[key] || DEFAULT_SETTINGS.byo_pricing[key]}
                            onChange={(e) => updateBYOPricing(key, e.target.value)}
                            className="pl-8 font-mono text-right bg-white"
                            data-testid={`byo-${key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* BYO Example */}
                  <div className="mt-3 p-3 bg-white rounded-lg border border-orange-200">
                    <p className="text-xs font-medium text-orange-700 mb-2">BYO Pricing Examples</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>1 print</span>
                        <span className="font-mono">${(settings.byo_pricing?.one_print || 35).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>2 prints</span>
                        <span className="font-mono">${(settings.byo_pricing?.two_prints || 50).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>4 prints</span>
                        <span className="font-mono">
                          ${((settings.byo_pricing?.three_prints || 65) + (settings.byo_pricing?.additional_print || 15)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-orange-600">
                    BYO uses flat pricing only. Standard retail print pricing does NOT apply.
                  </p>
                </CardContent>
              </Card>

              {/* Retail Preset Product Mapping */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Shirt className="w-5 h-5" />
                    Retail Presets
                  </CardTitle>
                  <CardDescription className="text-blue-700">
                    Configure quick-add preset buttons for Retail Sale (drag to reorder)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add new preset */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="New preset name (e.g., Kids Tee, Beanie)"
                      value={newPresetLabel}
                      onChange={(e) => setNewPresetLabel(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addPreset()}
                      className="flex-1 bg-white"
                    />
                    <Button onClick={addPreset} disabled={!newPresetLabel.trim()} className="gap-1">
                      <Plus className="w-4 h-4" />
                      Add
                    </Button>
                  </div>

                  {/* Preset list */}
                  {(settings.retail_presets || []).length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <Shirt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No presets configured yet</p>
                      <p className="text-xs">Add presets above to create quick-add buttons in Retail Sale</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...(settings.retail_presets || [])]
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((preset, idx) => {
                          const hasProduct = preset.product_id && preset.product_name;
                          const sortedPresets = [...(settings.retail_presets || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                          const isFirst = idx === 0;
                          const isLast = idx === sortedPresets.length - 1;
                          
                          return (
                            <div 
                              key={preset.id} 
                              className={`p-3 bg-white rounded-lg border ${preset.enabled ? 'border-blue-200' : 'border-gray-200 opacity-60'}`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                {/* Reorder buttons */}
                                <div className="flex flex-col">
                                  <button
                                    onClick={() => movePreset(preset.id, 'up')}
                                    disabled={isFirst}
                                    className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => movePreset(preset.id, 'down')}
                                    disabled={isLast}
                                    className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                </div>
                                
                                {/* Label input */}
                                <Input
                                  value={preset.label}
                                  onChange={(e) => updatePresetLabel(preset.id, e.target.value)}
                                  className="h-8 font-medium w-32"
                                />
                                
                                {/* Enabled toggle */}
                                <div className="flex items-center gap-1 ml-auto">
                                  <span className="text-xs text-gray-500">{preset.enabled ? 'On' : 'Off'}</span>
                                  <Switch
                                    checked={preset.enabled}
                                    onCheckedChange={() => togglePresetEnabled(preset.id)}
                                  />
                                </div>
                                
                                {/* Delete button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deletePreset(preset.id)}
                                  className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {/* Product mapping */}
                              {hasProduct ? (
                                <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                                  <div className="flex-1">
                                    <p className="text-sm">{preset.product_name}</p>
                                    <p className="text-xs text-green-600 font-mono">${preset.retail_price?.toFixed(2)}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => clearPresetProduct(preset.id)}
                                    className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="relative">
                                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                                  <Input
                                    placeholder="Search product to link..."
                                    value={presetSearchTerms[preset.id] || ""}
                                    onChange={(e) => searchProducts(preset.id, e.target.value)}
                                    className="pl-8 h-9 text-sm"
                                  />
                                  {activePresetSearch === preset.id && products.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-40 overflow-auto">
                                      {products.map((p) => (
                                        <div
                                          key={p.id}
                                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex justify-between items-center"
                                          onClick={() => selectPresetProduct(preset.id, p)}
                                        >
                                          <span>
                                            <span className="font-medium">{p.brand}</span> {p.product_name}
                                          </span>
                                          <span className="text-green-600 font-mono text-xs">
                                            ${(p.retail_price || p.default_cost)?.toFixed(2)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-[10px] text-amber-600 mt-1">
                                    ⚠️ No product mapped - will create $0 row
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                  
                  <div className="p-3 bg-white rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>How it works:</strong> Enabled presets appear as quick-add buttons in Retail Sale. 
                      Clicking a preset creates a garment row with the linked product's name and retail price.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Retail Price Rounding */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Circle className="w-5 h-5" />
                    Retail Price Rounding
                  </CardTitle>
                  <CardDescription>
                    Round the final customer-facing retail total
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Rounding Mode</Label>
                    <Select
                      value={settings.rounding?.mode || "none"}
                      onValueChange={(v) => setSettings({
                        ...settings,
                        rounding: { mode: v }
                      })}
                    >
                      <SelectTrigger className="w-full" data-testid="rounding-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROUNDING_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Rounding Examples */}
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <p className="text-xs font-medium text-slate-600 mb-2">Example Totals</p>
                    <div className="text-sm space-y-1 text-slate-700">
                      {[123.45, 127.89, 152.50].map((amount) => {
                        const rounded = (() => {
                          const mode = settings.rounding?.mode || "none";
                          if (mode === "none") return amount;
                          if (mode === "nearest_1") return Math.round(amount);
                          if (mode === "up_1") return Math.ceil(amount);
                          if (mode === "nearest_5") return Math.round(amount / 5) * 5;
                          return amount;
                        })();
                        return (
                          <div key={amount} className="flex justify-between">
                            <span className="text-muted-foreground">${amount.toFixed(2)}</span>
                            <span className="font-mono font-medium">${rounded.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Worked Example Card */}
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Calculator className="w-5 h-5" />
                  Worked Example
                </CardTitle>
                <CardDescription className="text-amber-700">
                  AS Colour Staple Tee ($30) with 3 prints: S, L, S
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-amber-800">Print Calculation:</p>
                    <div className="text-sm space-y-1 bg-white/60 p-3 rounded-lg">
                      <div className="flex justify-between">
                        <span>1st print (S)</span>
                        <span className="font-mono">${workedExample.print1.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-700">
                        <span>2nd print (L - ${settings.additional_print_discount.value} discount)</span>
                        <span className="font-mono">${workedExample.print2.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-700">
                        <span>3rd print (S - ${settings.additional_print_discount.value} discount)</span>
                        <span className="font-mono">${workedExample.print3.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium pt-2 border-t border-amber-200">
                        <span>Print Total</span>
                        <span className="font-mono">${workedExample.printTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-amber-800">Final Total:</p>
                    <div className="text-sm space-y-1 bg-white/60 p-3 rounded-lg">
                      <div className="flex justify-between">
                        <span>Print Total</span>
                        <span className="font-mono">${workedExample.printTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Garment (AS Colour Staple Tee)</span>
                        <span className="font-mono">${workedExample.garmentPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-amber-200 text-amber-900">
                        <span>TOTAL</span>
                        <span className="font-mono">${workedExample.total.toFixed(2)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      Expected: Print total = $49, Garment total = $79
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
